import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { AlertTriangle, Blocks, Plug, RefreshCcw, Wrench } from 'lucide-react';
import {
  useCodeAgentKillSwitches,
  useCodeAgentTools,
  useCodeAuditLogs,
} from '../../services/code-agent.service';
import {
  CodeAgentRiskLevel,
  CodeAgentToolOwner,
  CodeAgentToolRuntime,
  CodeAgentToolVisibilityStatus,
} from '../../types/code-agent';
import { EmptyState } from '../../components/admin/EmptyState';
import { formatDateTime } from '../../utils/dateFormat';

const STATUS_OPTIONS: Array<CodeAgentToolVisibilityStatus | 'all'> = [
  'all',
  'active',
  'disabled',
  'denied',
];
const RUNTIME_OPTIONS: CodeAgentToolRuntime[] = ['cli', 'mcp', 'server', 'sandbox'];
const OWNER_OPTIONS: CodeAgentToolOwner[] = ['core', 'server', 'mcp', 'plugin', 'project'];
const RISK_OPTIONS: CodeAgentRiskLevel[] = [
  'read',
  'write',
  'execute',
  'network',
  'destructive',
  'credential-sensitive',
];

const statusColor = (status: CodeAgentToolVisibilityStatus) => {
  if (status === 'active') return 'success';
  if (status === 'disabled') return 'default';
  return 'error';
};

const policyColor = (policy: string) => {
  if (policy === 'allow') return 'success';
  if (policy === 'ask') return 'warning';
  return 'error';
};

const listValue = (value: string[] | undefined) => (value?.length ? value.join(', ') : 'None');

export const CodeAgentToolsPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [statusFilter, setStatusFilter] = useState<CodeAgentToolVisibilityStatus | 'all'>('all');
  const [runtimeFilter, setRuntimeFilter] = useState<CodeAgentToolRuntime | ''>('');
  const [ownerFilter, setOwnerFilter] = useState<CodeAgentToolOwner | ''>('');
  const [riskFilter, setRiskFilter] = useState<CodeAgentRiskLevel | ''>('');

  const {
    data: toolsResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useCodeAgentTools(page + 1, pageSize, {
    status: statusFilter,
    runtime: runtimeFilter || undefined,
    owner: ownerFilter || undefined,
    risk_level: riskFilter || undefined,
  });
  const { data: policyResponse } = useCodeAgentKillSwitches();
  const {
    data: skillEventsResponse,
    isFetching: isFetchingSkillEvents,
    refetch: refetchSkillEvents,
  } = useCodeAuditLogs(1, 8, { eventType: 'code_agent.skill' });
  const {
    data: errorEventsResponse,
    isFetching: isFetchingErrors,
    refetch: refetchErrors,
  } = useCodeAuditLogs(1, 8, { eventType: 'failed' });

  const tools = toolsResponse?.data ?? [];
  const meta = toolsResponse?.meta;
  const policy = policyResponse?.data;
  const skillEvents = skillEventsResponse?.data ?? [];
  const errorEvents = errorEventsResponse?.data ?? [];

  const refreshAll = () => {
    refetch();
    refetchSkillEvents();
    refetchErrors();
  };

  const pluginPolicyRows = useMemo(
    () => [
      { id: 'allowed', label: 'Allowed plugins', value: listValue(policy?.allowedPluginNames) },
      { id: 'blocked', label: 'Blocked plugins', value: listValue(policy?.blockedPluginNames) },
      {
        id: 'skill-sources',
        label: 'Blocked skill sources',
        value: listValue(policy?.blockedSkillSources),
      },
      {
        id: 'skill-risks',
        label: 'Blocked skill risks',
        value: listValue(policy?.blockedSkillRiskLevels),
      },
    ],
    [policy],
  );

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Tool',
      flex: 1.3,
      minWidth: 230,
      renderCell: (params) => (
        <Box className="flex h-full min-w-0 flex-col justify-center">
          <Typography variant="body2" className="font-medium text-gray-900 dark:text-slate-100">
            {params.value}
          </Typography>
          <Typography variant="caption" className="truncate text-gray-500 dark:text-slate-400">
            {params.row.description || 'No description'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 105,
      renderCell: (params) => (
        <Chip size="small" color={statusColor(params.value)} label={String(params.value).toUpperCase()} />
      ),
    },
    { field: 'runtime', headerName: 'Runtime', width: 95 },
    { field: 'owner', headerName: 'Owner', width: 95 },
    {
      field: 'loading',
      headerName: 'Loading',
      width: 100,
      renderCell: (params) => (
        <Chip
          size="small"
          variant={params.value === 'deferred' ? 'outlined' : 'filled'}
          label={params.value ?? 'core'}
        />
      ),
    },
    {
      field: 'riskLevel',
      headerName: 'Risk',
      width: 145,
      renderCell: (params) => <Chip size="small" variant="outlined" label={params.value} />,
    },
    {
      field: 'effectivePolicy',
      headerName: 'Policy',
      width: 105,
      renderCell: (params) => (
        <Chip size="small" color={policyColor(params.value)} label={String(params.value).toUpperCase()} />
      ),
    },
    {
      field: 'usage',
      headerName: 'Usage',
      width: 210,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="body2" className="text-gray-700 dark:text-slate-300">
          {params.value.total} total / {params.value.failed} failed / {params.value.rejected} denied
        </Typography>
      ),
    },
    {
      field: 'deniedReasons',
      headerName: 'Denied Reasons',
      flex: 1,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => (
        <Typography variant="caption" className="truncate text-gray-600 dark:text-slate-400">
          {params.value?.length ? params.value.join(', ') : 'None'}
        </Typography>
      ),
    },
  ];

  return (
    <Box className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
      <Box className="mb-6 flex items-center justify-between gap-4">
        <Box>
          <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
            Code Agent Tool Diagnostics
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Inspect Code Agent visibility, skill activity, plugin policy, denials, and errors. Global tool controls live in Tool Management.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshCcw size={16} />}
          onClick={refreshAll}
          className="h-10 border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          Refresh
        </Button>
      </Box>

      <Box className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-4">
        {[
          { label: 'Total Tools', value: meta?.counts.total ?? 0, Icon: Wrench },
          { label: 'Active', value: meta?.counts.active ?? 0, Icon: Blocks },
          { label: 'Denied', value: meta?.counts.denied ?? 0, Icon: AlertTriangle },
          { label: 'Plugins Blocked', value: policy?.blockedPluginNames.length ?? 0, Icon: Plug },
        ].map(({ label, value, Icon }) => (
          <Box key={label} className="rounded-lg bg-white p-4 shadow-sm dark:bg-slate-800">
            <Box className="flex items-center justify-between">
              <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
                {label}
              </Typography>
              <Icon size={18} className="text-gray-500 dark:text-slate-400" />
            </Box>
            <Typography variant="h5" className="mt-2 font-semibold text-gray-900 dark:text-slate-100">
              {value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box className="mb-5 rounded-lg bg-white p-4 shadow-sm dark:bg-slate-800">
        <Box className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <FormControl size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(event) => setStatusFilter(event.target.value as CodeAgentToolVisibilityStatus | 'all')}
            >
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Runtime</InputLabel>
            <Select
              value={runtimeFilter}
              label="Runtime"
              onChange={(event) => setRuntimeFilter(event.target.value as CodeAgentToolRuntime | '')}
            >
              <MenuItem value="">All</MenuItem>
              {RUNTIME_OPTIONS.map((runtime) => (
                <MenuItem key={runtime} value={runtime}>
                  {runtime}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Owner</InputLabel>
            <Select
              value={ownerFilter}
              label="Owner"
              onChange={(event) => setOwnerFilter(event.target.value as CodeAgentToolOwner | '')}
            >
              <MenuItem value="">All</MenuItem>
              {OWNER_OPTIONS.map((owner) => (
                <MenuItem key={owner} value={owner}>
                  {owner}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Risk</InputLabel>
            <Select
              value={riskFilter}
              label="Risk"
              onChange={(event) => setRiskFilter(event.target.value as CodeAgentRiskLevel | '')}
            >
              <MenuItem value="">All</MenuItem>
              {RISK_OPTIONS.map((risk) => (
                <MenuItem key={risk} value={risk}>
                  {risk}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box className="mb-5 rounded-lg bg-white shadow-sm dark:bg-slate-800">
        {isLoading ? (
          <Box className="flex items-center justify-center p-12">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box className="p-12 text-center text-red-600 dark:text-red-400">
            Failed to load Code Agent tools.
          </Box>
        ) : tools.length > 0 ? (
          <DataGrid
            rows={tools}
            columns={columns}
            getRowId={(row) => row.publicId}
            loading={isFetching}
            disableRowSelectionOnClick
            paginationMode="server"
            rowCount={meta?.total ?? tools.length}
            pageSizeOptions={[25, 50, 100]}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            autoHeight
            className="border-0"
          />
        ) : (
          <Box className="p-12">
            <EmptyState Icon={Wrench} title="No Tools" description="No Code Agent tools match the selected filters." />
          </Box>
        )}
      </Box>

      <Box className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Box className="rounded-lg bg-white p-4 shadow-sm dark:bg-slate-800">
          <Typography variant="h6" className="mb-3 font-semibold text-gray-900 dark:text-slate-100">
            Plugin Policy
          </Typography>
          <Box className="space-y-3">
            {pluginPolicyRows.map((row) => (
              <Box key={row.id}>
                <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                  {row.label}
                </Typography>
                <Typography variant="body2" className="break-words text-gray-900 dark:text-slate-100">
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box className="rounded-lg bg-white p-4 shadow-sm dark:bg-slate-800">
          <Typography variant="h6" className="mb-3 font-semibold text-gray-900 dark:text-slate-100">
            Skill Events
          </Typography>
          {isFetchingSkillEvents ? (
            <CircularProgress size={24} />
          ) : skillEvents.length ? (
            <Box className="space-y-3">
              {skillEvents.map((event) => (
                <Box key={event.publicId} className="border-b border-gray-100 pb-2 last:border-b-0 dark:border-slate-700">
                  <Typography variant="body2" className="font-medium text-gray-900 dark:text-slate-100">
                    {event.eventType}
                  </Typography>
                  <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                    {formatDateTime(event.createdAt, true)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
              No recent skill events.
            </Typography>
          )}
        </Box>

        <Box className="rounded-lg bg-white p-4 shadow-sm dark:bg-slate-800">
          <Typography variant="h6" className="mb-3 font-semibold text-gray-900 dark:text-slate-100">
            Errors And Denials
          </Typography>
          {isFetchingErrors ? (
            <CircularProgress size={24} />
          ) : errorEvents.length ? (
            <Box className="space-y-3">
              {errorEvents.map((event) => (
                <Box key={event.publicId} className="border-b border-gray-100 pb-2 last:border-b-0 dark:border-slate-700">
                  <Typography variant="body2" className="font-medium text-gray-900 dark:text-slate-100">
                    {event.eventType}
                  </Typography>
                  <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                    {formatDateTime(event.createdAt, true)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" className="text-gray-500 dark:text-slate-400">
              No recent failed events.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
